<template>
  <v-autocomplete
    v-model="searchText"
    :label="label"
    :items="selectItems"
    item-title="text"
    item-value="id"
    :placeholder="placeholder"
    :disabled="disabled"
    :error="error"
    :hide-details="hideDetails"
    :error-messages="errorMessages"
    :custom-filter="customFilter"
    :clearable="clearable"
    color="fieldOutlineColor"
    variant="outlined"
    density="compact"
    @update:model-value="emitNewValue"
    @blur="validateSelection"
  />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { type SelectItem } from "@/models/selectItem";

const props = withDefaults(
  defineProps<{
    items: SelectItem[] | string[];
    selectText?: string | object;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    clearable?: boolean;
    error?: boolean;
    errorMessages?: string;
    hideDetails?: boolean;
  }>(),
  {
    clearable: true,
    hideDetails: false,
  }
);

const emit = defineEmits<{
  (e: "fieldUpdated", newValue: string): void;
}>();

const selectItems = computed(() =>
  props.items.map((i) =>
    typeof i === "string" ? ({ id: i, text: i } as SelectItem) : i
  )
);

const searchText = ref(getItem((props.selectText as unknown as string) ?? ""));

function emitNewValue(newValue: string): void {
  searchText.value = newValue ?? "";
  emit("fieldUpdated", searchText.value);
}

function customFilter(item: string, queryText: string): boolean {
  searchText.value = queryText;
  return item.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase());
}

function validateSelection(): void {
  emitNewValue(getItem(searchText.value));
}

function getItem(searchItem: string): string {
  const idx = selectItems.value.findIndex(
    (item: SelectItem) =>
      item.id.toLocaleLowerCase() === searchItem.toLocaleLowerCase()
  );
  return idx === -1 ? "" : selectItems.value[idx].id;
}
</script>
